import type {
  Map as MapLibreMap,
  GeoJSONSource,
  ExpressionSpecification,
  SymbolLayerSpecification,
} from "maplibre-gl";
import type { City } from "../types";
import { hasMapLocation } from "../utils/geo";
import type { AtlasConfig } from "./atlas-config";
import {
  assignStableMinZoom,
  bandFor,
  mercX,
  mercY,
  type StablePlacement,
} from "./placement";

const SOURCE_ID = "concerts";
const HULL_SOURCE_ID = "cluster-hull";
const CLUSTER_LAYER = "clusters";
const CLUSTER_COUNT_LAYER = "cluster-count";
const POINT_LAYER = "concert-points";
const POINT_COUNT_LAYER = "concert-point-count";
const HULL_FILL_LAYER = "cluster-hull-fill";
const HULL_LINE_LAYER = "cluster-hull-line";
// Atlas mode: fused symbol+name markers. Two layers because capitals get their own
// typography (Medium, spaced caps) — text-font is not data-driven per feature.
const ATLAS_CAPITAL_LAYER = "atlas-capitals";
const ATLAS_CITY_LAYER = "atlas-cities";
const ATLAS_TOWN_LAYER = "atlas-towns";
const ATLAS_COUNT_LAYER = "atlas-counts";
/** Isolation-exempt towns: identical to ATLAS_TOWN_LAYER but with no zoom floor, because
 *  a layer minzoom is uniform and the exemption is per-feature (see orphanRadius). */
const ATLAS_ORPHAN_LAYER = "atlas-orphan-towns";

/** Cities the packer suppressed at the current band — the "there is more here" cue.
 *  One source feeds both residual modes; the stipple layer draws the grid-capped subset,
 *  the wash reads every point. */
const RESIDUAL_SOURCE_ID = "concerts-residual";
const RESIDUAL_WASH_LAYER = "residual-wash";
const RESIDUAL_STIPPLE_LAYER = "residual-stipple";

/** Basemap layer concert circles render beneath in DOTS mode, so city labels stay
 *  legible over dense circle fields. Atlas-mode symbols go on TOP instead: fused
 *  symbols must claim collision space before the basemap gazetteer does. */
const LABEL_LAYER_ID = "place-labels-minor";

/** Coarse pointers (phones/tablets) get larger targets. Checked once — pointer class
 *  doesn't change mid-session in any case that matters. */
export const COARSE_POINTER =
  typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
const TOUCH_BUMP = COARSE_POINTER ? 2.5 : 0;

/** Colors for dark and light themes (markers and their labels). */
const MARKER_COLORS = {
  dark: { fill: "#f0c040", hover: "#ffd700", text: "#1a1a2e", halo: "#f0c040" },
  light: { fill: "#7d2b3c", hover: "#5f1f2d", text: "#ffffff", halo: "#7d2b3c" },
};

/** Atlas-mode label + glyph palette: markers speak in the accent so concert cities read
 *  as *data* against the muted gazetteer, with the theme's land color as knockout.
 *  Light glyphs are black, not burgundy (2026-09-06): the accent's red component is
 *  invisible to a red-green colour-blind reader, who saw "black dots" anyway — so the
 *  hue bought nothing and cost contrast. Labels follow, in a neutral near-black. */
const ATLAS_COLORS = {
  dark: { glyph: "#f0c040", knockout: "#1e2a45", label: "#f2d685", capital: "#ffe28a", halo: "#1e2a45" },
  light: { glyph: "#101010", knockout: "#eef1f6", label: "#2a3140", capital: "#111827", halo: "#eef1f6" },
};

/** Concert count of a marker, whether clustered (sum) or a single city. */
const COUNT_EXPR: ExpressionSpecification = [
  "coalesce",
  ["get", "totalConcerts"],
  ["get", "count"],
];

/** Palette anchors of the density ramp, dimmest → most vivid. `PALETTE_U` places them on
 *  a 0..1 axis mirroring the original absolute count stops (1/50/200/600), so a full-depth
 *  ramp reproduces the original colors at the original counts exactly. */
const DENSITY_PALETTE = {
  dark: ["#8f7d3f", "#bda13d", "#e7c33a", "#ffd700"], // dim gold → vivid gold
  light: ["#d9a9b3", "#b66579", "#8f3349", "#5c1322"], // pale rose → deep burgundy
};
const PALETTE_U = [0, 49 / 599, 199 / 599, 1];
/** Count at which the absolute ramp saturated; also the max the ramp normalizes to. */
const FULL_RAMP_MAX = 600;

function lerpHex(a: string, b: string, t: number): string {
  const ca = parseInt(a.slice(1), 16);
  const cb = parseInt(b.slice(1), 16);
  const ch = (shift: number) => {
    const va = (ca >> shift) & 0xff;
    const vb = (cb >> shift) & 0xff;
    return Math.round(va + (vb - va) * t);
  };
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, "0")}`;
}

/** Color at position u (0 = dimmest, 1 = most vivid) along the theme's palette. */
function paletteAt(theme: "dark" | "light", u: number): string {
  const colors = DENSITY_PALETTE[theme];
  const c = Math.min(1, Math.max(0, u));
  for (let i = 1; i < PALETTE_U.length; i++) {
    if (c <= PALETTE_U[i]) {
      const t = (c - PALETTE_U[i - 1]) / (PALETTE_U[i] - PALETTE_U[i - 1]);
      return lerpHex(colors[i - 1], colors[i], t);
    }
  }
  return colors[colors.length - 1];
}

/** Single-hue intensity ramp normalized to the densest city on the map — see the v1
 *  design notes: a sparse filtered map keeps its densest city vivid instead of fading
 *  everything to the faint end of an absolute ramp. */
function densityColor(
  theme: "dark" | "light",
  maxCount = FULL_RAMP_MAX
): ExpressionSpecification {
  const M = Math.max(2, Math.min(FULL_RAMP_MAX, maxCount));
  const floorU = 0.45 * (1 - Math.log(M) / Math.log(FULL_RAMP_MAX));
  const stops: (number | string)[] = [1, paletteAt(theme, floorU)];
  for (const u of PALETTE_U.slice(1, -1)) {
    if (u <= floorU) continue;
    const n = 1 + ((u - floorU) / (1 - floorU)) * (M - 1);
    if (n > 1 && n < M) stops.push(n, paletteAt(theme, u));
  }
  stops.push(M, paletteAt(theme, 1));
  return ["interpolate", ["linear"], COUNT_EXPR, ...stops] as ExpressionSpecification;
}

/** Count-label color readable across the intensity ramp (dots mode). */
function densityTextColor(
  theme: "dark" | "light",
  maxCount = FULL_RAMP_MAX
): ExpressionSpecification | string {
  if (theme === "dark") return "#211a05";
  const M = Math.max(2, Math.min(FULL_RAMP_MAX, maxCount));
  const floorU = 0.45 * (1 - Math.log(M) / Math.log(FULL_RAMP_MAX));
  const FLIP_U = 99 / 599;
  if (floorU >= FLIP_U) return "#ffffff";
  const flipCount = 1 + ((FLIP_U - floorU) / (1 - floorU)) * (M - 1);
  return ["step", COUNT_EXPR, "#43101d", flipCount, "#ffffff"];
}

export function getMarkerColors(theme: "dark" | "light") {
  return MARKER_COLORS[theme];
}

/** The concert-marker layers that answer clicks/hovers under the current mode. Layers
 *  are looked up (never assumed) so callers can safely pass the result to
 *  queryRenderedFeatures whichever mode built the style. */
export function interactivePointLayers(map: MapLibreMap): string[] {
  // The residual layers are deliberately absent: a wash and a 1.5px stipple are texture,
  // not places — they carry no identity to put in a popup, and making them tappable
  // would promise a card that has nothing to say. Orphan towns ARE places (full label,
  // real counts), so they answer clicks exactly like any other town.
  return [
    POINT_LAYER,
    ATLAS_CAPITAL_LAYER,
    ATLAS_CITY_LAYER,
    ATLAS_TOWN_LAYER,
    ATLAS_ORPHAN_LAYER,
  ].filter((id) => map.getLayer(id));
}

// ── Atlas glyph icons ─────────────────────────────────────────────

const ICON_PIXEL_RATIO = 2;
/** Glyph diameters in CSS px (drawn at 2×). Tier 1 is the "musical capital" symbol. */
const TIER_DIAMETER = { 1: 15, 2: 10, 3: 7.5 };

function drawGlyph(
  kind: AtlasConfig["markers"]["capitalGlyph"] | "filled" | "hollow",
  diameter: number,
  glyphColor: string,
  knockout: string
): ImageData {
  const pad = 3;
  const size = Math.ceil((diameter + pad * 2) * ICON_PIXEL_RATIO);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(ICON_PIXEL_RATIO, ICON_PIXEL_RATIO);
  const c = (diameter + pad * 2) / 2;
  const r = diameter / 2;

  const circle = (radius: number) => {
    ctx.beginPath();
    ctx.arc(c, c, radius, 0, Math.PI * 2);
  };

  switch (kind) {
    case "rings": {
      // Classic capital symbol: outer ring + center dot.
      circle(r);
      ctx.lineWidth = 1.8;
      ctx.strokeStyle = glyphColor;
      ctx.stroke();
      circle(r * 0.42);
      ctx.fillStyle = glyphColor;
      ctx.fill();
      break;
    }
    case "star": {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? r : r * 0.45;
        const a = (Math.PI / 5) * i - Math.PI / 2;
        const x = c + radius * Math.cos(a);
        const y = c + radius * Math.sin(a);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = glyphColor;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = knockout;
      ctx.stroke();
      break;
    }
    case "diamond": {
      ctx.beginPath();
      ctx.moveTo(c, c - r);
      ctx.lineTo(c + r, c);
      ctx.lineTo(c, c + r);
      ctx.lineTo(c - r, c);
      ctx.closePath();
      ctx.fillStyle = glyphColor;
      ctx.fill();
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = knockout;
      ctx.stroke();
      break;
    }
    case "dot":
    case "filled": {
      circle(r);
      ctx.fillStyle = glyphColor;
      ctx.fill();
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = knockout;
      ctx.stroke();
      break;
    }
    case "hollow": {
      circle(r - 0.8);
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = glyphColor;
      ctx.stroke();
      break;
    }
  }
  return ctx.getImageData(0, 0, size, size);
}

/** (Re)register the three tier glyphs on the map. Style reloads wipe images, and every
 *  config/theme change reloads the style, so images here are always drawn fresh for the
 *  current glyph choice + theme. */
function ensureAtlasIcons(map: MapLibreMap, theme: "dark" | "light", cfg: AtlasConfig) {
  const colors = ATLAS_COLORS[theme];
  const icons: [string, ImageData][] = [
    ["atlas-t1", drawGlyph(cfg.markers.capitalGlyph, TIER_DIAMETER[1], colors.glyph, colors.knockout)],
    ["atlas-t2", drawGlyph("filled", TIER_DIAMETER[2], colors.glyph, colors.knockout)],
    ["atlas-t3", drawGlyph("hollow", TIER_DIAMETER[3], colors.glyph, colors.knockout)],
  ];
  for (const [name, img] of icons) {
    if (map.hasImage(name)) map.removeImage(name);
    map.addImage(name, img, { pixelRatio: ICON_PIXEL_RATIO });
  }
}

// ── Layer construction ────────────────────────────────────────────

/** Add concert marker source and layers to the map, per the config's marker mode.
 *
 *  Idempotent by construction. Two config changes in the same tick (a Lab preset and a
 *  theme flip, say) issue two setStyle calls but leave TWO `style.load` handlers armed,
 *  and both fire against the single reloaded style: without the clear, the second call
 *  threw "Source already exists", and skipping it instead would have stranded the map on
 *  the FIRST change's layers while the config had already moved on. Rebuilding wins. */
export function addConcertLayers(
  map: MapLibreMap,
  theme: "dark" | "light",
  cfg: AtlasConfig
) {
  removeConcertLayers(map);
  if (cfg.markers.mode === "atlas") {
    addAtlasLayers(map, theme, cfg);
  } else {
    addDotLayers(map, theme);
  }
}

/** `#rrggbb` at a given alpha. Heatmap ramps need real transparency at density 0 — a
 *  layer-wide opacity property cannot express "fade to nothing at the edges". */
function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}, ${alpha})`;
}

/** First of `ids` present in the current style. Insertion points vary by preset (no
 *  urban tint, no rivers, no coastline …) and addLayer throws on an unknown beforeId. */
function firstLayer(map: MapLibreMap, ids: string[]): string | undefined {
  return ids.find((id) => map.getLayer(id));
}

/** The residual cue: everything the packer suppressed at the current band, drawn as
 *  texture rather than as markers. Both modes read one source; updateMarkers keeps it
 *  filled with the complement of what the symbol layers are showing.
 *
 *  Why the residual and not simply "all cities": a field fed by every city just restates
 *  what the big glyphs already say (Paris is large). Fed by the SUPPRESSED set, Paris
 *  contributes nothing and its forty unlabeled neighbours are the entire signal — the
 *  layer renders exactly "what you are not being shown", and fades out by itself as
 *  deeper zoom moves cities out of the residual and into the symbol layers. */
function addResidualLayers(map: MapLibreMap, theme: "dark" | "light", cfg: AtlasConfig) {
  const mode = cfg.markers.residual;
  if (mode === "off") return;
  const colors = ATLAS_COLORS[theme];
  const strength = cfg.markers.residualStrength;

  map.addSource(RESIDUAL_SOURCE_ID, {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  if (mode === "wash" || mode === "both") {
    map.addLayer(
      {
        id: RESIDUAL_WASH_LAYER,
        type: "heatmap",
        source: RESIDUAL_SOURCE_ID,
        paint: {
          // Presence first, magnitude a distant second: the fact that a place exists
          // here is the message, and a suppressed town with 30 concerts should not read
          // thirty times louder than one with 1 — that's the count ramp's job, on the
          // markers, where the reader can click through to what it means.
          "heatmap-weight": [
            "interpolate", ["linear"], ["get", "count"],
            1, 0.7,
            25, 1.3,
          ],
          "heatmap-intensity": 0.55,
          // Radius tracks the label footprint it stands in for: at continental zoom a
          // suppressed town speaks for its whole district, close in it speaks for
          // itself.
          "heatmap-radius": [
            "interpolate", ["linear"], ["zoom"],
            1, 10,
            4, 16,
            7, 26,
            10, 38,
          ],
          "heatmap-color": [
            "interpolate", ["linear"], ["heatmap-density"],
            0, rgba(colors.glyph, 0),
            0.2, rgba(colors.glyph, 0.05),
            0.6, rgba(colors.glyph, 0.13),
            1, rgba(colors.glyph, 0.2),
          ],
          "heatmap-opacity": Math.min(1, 0.85 * strength),
        },
      },
      // Above the land fill, below everything that carries meaning. `coastline` (added
      // by the style builder whenever a wash is on) then redraws the shore over it, so
      // the field bleeding a few px into the sea never softens the coast itself.
      firstLayer(map, ["coastline", "urban", "lakes", "borders", LABEL_LAYER_ID])
    );
  }

  if (mode === "stipple" || mode === "both") {
    map.addLayer(
      {
        id: RESIDUAL_STIPPLE_LAYER,
        type: "circle",
        source: RESIDUAL_SOURCE_ID,
        // One dot per grid cell (updateMarkers picks the densest city per cell), so the
        // mark reads as an even texture instead of clumping into pseudo-clusters that
        // look like places.
        filter: ["==", ["get", "stipple"], 1],
        paint: {
          "circle-color": colors.glyph,
          // Must stay visibly SMALLER and fainter than the tier-3 town glyph (~3.9px
          // radius at z3) — the whole bet is that a stipple this light reads as texture.
          // The v1 measles field was not a matter of dot count but of dot weight.
          // Tuned against a screenshot diff: 1.1px @ 22% was so faint it changed barely
          // a thousand pixels of a 1400×900 frame — invisible is not subtle, it's just
          // absent, and it would have rigged the comparison against the mode.
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            2, 1.5,
            6, 2,
            10, 2.4,
          ],
          "circle-opacity": Math.min(1, 0.38 * strength),
          "circle-stroke-width": 0,
        },
      },
      // Below the basemap gazetteer: the stipple must never compete with a name.
      firstLayer(map, [LABEL_LAYER_ID])
    );
  }
}

function addAtlasLayers(map: MapLibreMap, theme: "dark" | "light", cfg: AtlasConfig) {
  const colors = ATLAS_COLORS[theme];
  const m = cfg.markers;
  ensureAtlasIcons(map, theme, cfg);
  addResidualLayers(map, theme, cfg);

  // No clustering: the fused symbols self-declutter through label collision, with
  // symbol-sort-key giving bigger cities first claim. That is the atlas contract —
  // what renders is always a named place, never an anonymous blob.
  map.addSource(SOURCE_ID, {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  const sizeScale = m.sizeScale * (COARSE_POINTER ? 1.18 : 1);
  const iconSize: ExpressionSpecification = [
    "interpolate", ["linear"], ["zoom"],
    3, 0.72 * sizeScale,
    6, 1 * sizeScale,
  ];
  const sortKey: ExpressionSpecification = ["*", -1, ["get", "count"]];
  // With labelRequired, a symbol whose name can't be placed renders nothing at all —
  // "unlabeled dot numbers" become structurally impossible. text-optional=true is the
  // escape hatch lever (icon may render alone under collision).
  const textOptional = !m.labelRequired;
  // Stable placement filters by tier only. The minz gating deliberately does NOT use a
  // ["zoom"] filter expression: filters evaluate per TILE at bucket-build time, so tiles
  // built under a different covering zoom excluded minz-boundary cities in tile-shaped
  // zones (Greensboro vanished while Tarboro survived — non-priority, region-shaped).
  // Instead updateMarkers setData()s only the cities admitted at the current integer
  // zoom band — the visible set is then a pure function of (data, band), and panning
  // cannot change it.
  const tierFilter = (tier: number): ExpressionSpecification => [
    "==",
    ["get", "tier"],
    tier,
  ];
  // Stable placement pins each label to the side the packer chose (`aside` property:
  // +1 = name right of dot, -1 = left) so the real rendered footprint matches
  // placement.ts's boxes — free-floating variable anchors let the engine settle labels
  // into positions the packing never modeled, and the leftover conflicts get arbitrated
  // by the viewport again (the pan-reshuffle bug). Dynamic mode keeps flexible anchors.
  const anchorLayout = (
    radialOffset: number
  ): Partial<NonNullable<SymbolLayerSpecification["layout"]>> =>
    m.placement === "stable"
      ? {
          // In stable mode the PACKER is the collision authority: MapLibre must never
          // hide an admitted symbol, or zoom drift during a drag re-arbitrates marginal
          // pairs and labels flicker in and out mid-pan (the residual instability after
          // every other fix). allow-overlap renders admitted symbols unconditionally;
          // ignore-placement stays false so markers still claim space and the basemap
          // gazetteer yields to them. A rare estimate miss now shows as slight label
          // crowding — strictly better than viewport-dependent vanishing.
          "text-allow-overlap": true,
          "icon-allow-overlap": true,
          // `aside` anchor codes come from placement.ts (ANCHOR_*): 0 left (text right
          // of dot), 1 right, 2 top (text below), 3 bottom (text above).
          "text-anchor": [
            "match", ["get", "aside"],
            1, "right",
            2, "top",
            3, "bottom",
            "left",
          ] as unknown as "left",
          "text-offset": [
            "match", ["get", "aside"],
            1, ["literal", [-0.7, 0]],
            2, ["literal", [0, 0.55]],
            3, ["literal", [0, -0.55]],
            ["literal", [0.7, 0]],
          ] as unknown as [number, number],
        }
      : {
          "text-variable-anchor": ["left", "right", "top", "bottom"],
          "text-radial-offset": radialOffset,
          "text-justify": "auto",
        };

  // Tier 3 towns first (added lowest → last claim on collision space), staged in by
  // the townMinzoom lever: a one-concert village must not crowd the continental view —
  // that's the v1 "measles field" in new clothes. It joins the map once the reader has
  // committed to a region.
  const townLayer = (
    id: string,
    minzoom: number,
    orphan: boolean
  ): SymbolLayerSpecification => ({
    id,
    type: "symbol",
    source: SOURCE_ID,
    minzoom,
    filter: [
      "all",
      tierFilter(3),
      [orphan ? "==" : "!=", ["get", "orphan"], 1],
    ] as ExpressionSpecification,
    layout: {
      "icon-image": "atlas-t3",
      "icon-size": iconSize,
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        3, 9.5 * m.labelScale,
        6, 11.5 * m.labelScale,
        10, 14 * m.labelScale,
      ],
      ...anchorLayout(0.5),
      "text-max-width": 8,
      "text-optional": textOptional,
      "symbol-sort-key": sortKey,
    },
    paint: {
      "text-color": colors.label,
      "text-halo-color": colors.halo,
      "text-halo-width": 1.3,
    },
  });

  map.addLayer(townLayer(ATLAS_TOWN_LAYER, m.townMinzoom, false));
  // Same glyph, same voice, no floor. The `orphan` flag is per-BAND (updateMarkers sets
  // it while the band is below the floor), so a town rendered here at z2 moves back into
  // the ordinary town layer at z4 with no change in appearance — the reader sees one
  // continuous place, not a marker that swaps identity as it zooms.
  map.addLayer(townLayer(ATLAS_ORPHAN_LAYER, 0, true));

  // Tier 2 cities. Above every basemap label (no beforeId): data outranks gazetteer.
  map.addLayer({
    id: ATLAS_CITY_LAYER,
    type: "symbol",
    source: SOURCE_ID,
    filter: tierFilter(2),
    layout: {
      "icon-image": "atlas-t2",
      "icon-size": iconSize,
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        3, 10 * m.labelScale,
        6, 12.5 * m.labelScale,
        10, 15 * m.labelScale,
      ],
      ...anchorLayout(0.55),
      "text-max-width": 8,
      "text-optional": textOptional,
      "symbol-sort-key": sortKey,
    },
    paint: {
      "text-color": colors.label,
      "text-halo-color": colors.halo,
      "text-halo-width": 1.4,
    },
  });

  // Tier 1 — musical capitals: their own layer for the spaced-caps Medium voice.
  // Added after tier 2/3 so capitals claim collision space first.
  map.addLayer({
    id: ATLAS_CAPITAL_LAYER,
    type: "symbol",
    source: SOURCE_ID,
    filter: tierFilter(1),
    layout: {
      "icon-image": "atlas-t1",
      "icon-size": iconSize,
      "text-field": m.capitalCaps
        ? ["upcase", ["get", "name"]]
        : ["get", "name"],
      "text-font": ["Noto Sans Medium"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        3, 11 * m.labelScale,
        6, 14.5 * m.labelScale,
        10, 18 * m.labelScale,
      ],
      "text-letter-spacing": m.capitalCaps ? 0.14 : 0.02,
      ...anchorLayout(0.7),
      "text-max-width": 8,
      "text-optional": textOptional,
      "symbol-sort-key": sortKey,
    },
    paint: {
      "text-color": colors.capital,
      "text-halo-color": colors.halo,
      "text-halo-width": 1.5,
    },
  });

  if (m.showCounts) {
    map.addLayer({
      id: ATLAS_COUNT_LAYER,
      type: "symbol",
      source: SOURCE_ID,
      minzoom: 4.5,
      layout: {
        "text-field": ["get", "count"],
        "text-font": ["Noto Sans Regular"],
        "text-size": 9.5,
        "text-offset": [0, 1.15],
        "text-anchor": "top",
        "symbol-sort-key": sortKey,
      },
      paint: {
        "text-color": colors.label,
        "text-halo-color": colors.halo,
        "text-halo-width": 1.2,
        "text-opacity": 0.85,
      },
    });
  }
}

function addDotLayers(map: MapLibreMap, theme: "dark" | "light") {
  const beforeId = map.getLayer(LABEL_LAYER_ID) ? LABEL_LAYER_ID : undefined;

  map.addSource(SOURCE_ID, {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
    cluster: true,
    clusterMaxZoom: 9,
    // Very tight radius: only near-coincident cities merge, so a cluster
    // never absorbs concerts from a city the viewer would read as a separate
    // place (e.g. London must not swallow Paris at mid zoom).
    clusterRadius: 10,
    clusterProperties: {
      totalConcerts: ["+", ["get", "count"]],
    },
  });

  // Hull shown when hovering a cluster: the actual area its cities span.
  const colors = MARKER_COLORS[theme];
  map.addSource(HULL_SOURCE_ID, {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  map.addLayer(
    {
      id: HULL_FILL_LAYER,
      type: "fill",
      source: HULL_SOURCE_ID,
      paint: {
        "fill-color": colors.fill,
        "fill-opacity": 0.08,
      },
    },
    beforeId
  );
  map.addLayer(
    {
      id: HULL_LINE_LAYER,
      type: "line",
      source: HULL_SOURCE_ID,
      paint: {
        "line-color": colors.fill,
        "line-width": 1.5,
        "line-dasharray": [3, 2],
        "line-opacity": 0.8,
      },
    },
    beforeId
  );

  // Cluster circles (below basemap labels so city labels remain visible)
  map.addLayer(
    {
      id: CLUSTER_LAYER,
      type: "circle",
      source: SOURCE_ID,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": densityColor(theme),
        "circle-opacity": 0.92,
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          3, ["step", ["get", "point_count"], 5 + TOUCH_BUMP, 10, 6 + TOUCH_BUMP, 50, 7 + TOUCH_BUMP],
          6, ["step", ["get", "point_count"], 12 + TOUCH_BUMP, 10, 13 + TOUCH_BUMP, 50, 14 + TOUCH_BUMP],
        ],
        "circle-stroke-width": 3,
        "circle-stroke-color": densityColor(theme),
        "circle-stroke-opacity": 0.25,
      },
    },
    beforeId
  );

  // Individual city markers (below labels, same reason)
  map.addLayer(
    {
      id: POINT_LAYER,
      type: "circle",
      source: SOURCE_ID,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": densityColor(theme),
        "circle-radius": [
          "interpolate", ["linear"], ["zoom"],
          3, ["interpolate", ["linear"], ["get", "count"], 1, 3.5 + TOUCH_BUMP, 300, 6 + TOUCH_BUMP],
          6, ["interpolate", ["linear"], ["get", "count"], 1, 8 + TOUCH_BUMP, 50, 9.5 + TOUCH_BUMP, 300, 11 + TOUCH_BUMP],
        ],
        "circle-stroke-width": 3,
        "circle-stroke-color": densityColor(theme),
        "circle-stroke-opacity": 0.25,
      },
    },
    beforeId
  );

  // Count labels appear only once circles are big enough to hold them.
  map.addLayer({
    id: CLUSTER_COUNT_LAYER,
    type: "symbol",
    source: SOURCE_ID,
    minzoom: 4.5,
    filter: ["has", "point_count"],
    layout: {
      "text-field": ["get", "totalConcerts"],
      "text-font": ["Noto Sans Medium"],
      "text-size": 11,
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-color": densityTextColor(theme),
      "text-halo-color": densityColor(theme),
      "text-halo-width": 1.2,
    },
  });

  map.addLayer({
    id: POINT_COUNT_LAYER,
    type: "symbol",
    source: SOURCE_ID,
    minzoom: 4.5,
    filter: ["!", ["has", "point_count"]],
    layout: {
      "text-field": ["get", "count"],
      "text-font": ["Noto Sans Medium"],
      "text-size": 10,
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-color": densityTextColor(theme),
      "text-halo-color": densityColor(theme),
      "text-halo-width": 1.2,
    },
  });
}

/** Show the convex hull of a cluster's member cities while hovering it (dots mode).
 *  Handlers are plain (not layer-delegated) with explicit layer guards, so they are
 *  inert whenever the current mode has no cluster layer. */
export function attachClusterHull(map: MapLibreMap) {
  let activeClusterId: number | null = null;
  let requestSeq = 0;

  const clear = () => {
    activeClusterId = null;
    requestSeq++;
    const source = map.getSource(HULL_SOURCE_ID) as GeoJSONSource | undefined;
    source?.setData({ type: "FeatureCollection", features: [] });
  };

  map.on("mousemove", (e) => {
    if (!map.getLayer(CLUSTER_LAYER)) return;
    const features = map.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER] });
    const feature = features[0];
    if (!feature) {
      if (activeClusterId !== null) clear();
      return;
    }
    const clusterId = feature.properties?.cluster_id as number | undefined;
    if (clusterId === undefined || clusterId === activeClusterId) return;

    activeClusterId = clusterId;
    const seq = ++requestSeq;
    const source = map.getSource(SOURCE_ID) as GeoJSONSource;
    source
      .getClusterLeaves(clusterId, 10000, 0)
      .then((leaves) => {
        if (seq !== requestSeq) return; // hover moved on
        const points = leaves.map(
          (leaf) => (leaf.geometry as GeoJSON.Point).coordinates as [number, number]
        );
        const hull = convexHull(points);
        const hullSource = map.getSource(HULL_SOURCE_ID) as GeoJSONSource | undefined;
        if (!hullSource) return;
        if (hull.length < 3) {
          hullSource.setData({ type: "FeatureCollection", features: [] });
          return;
        }
        hullSource.setData({
          type: "Feature",
          geometry: { type: "Polygon", coordinates: [[...hull, hull[0]]] },
          properties: {},
        });
      })
      .catch(() => {
        /* cluster may no longer exist after a zoom — ignore */
      });
  });

  // Hull positions go stale once zooming or new data changes the clustering.
  map.on("zoomstart", clear);
  map.on("sourcedata", (e) => {
    if (e.sourceId === SOURCE_ID && e.sourceDataType === "content") clear();
  });
}

/** Monotone-chain convex hull on [lon, lat] points. */
function convexHull(points: [number, number][]): [number, number][] {
  if (points.length < 3) return points;
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (
    o: [number, number],
    a: [number, number],
    b: [number, number]
  ) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const lower: [number, number][] = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: [number, number][] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

/** Tier assignment for atlas mode: tier 1 = the top `capitalCount` cities by count
 *  (that also clear the tier-2 floor, so a sparse filtered map doesn't crown hamlets),
 *  tier 2 = at/above `tier2Min`, tier 3 = the rest. Relative to the *current* data, so
 *  a composer filter re-crowns its own capitals — same philosophy as the v1 ramp
 *  normalization. */
function assignTiers(cities: City[], cfg: AtlasConfig): globalThis.Map<number, number> {
  const m = cfg.markers;
  const tiers = new globalThis.Map<number, number>();
  const byCount = [...cities].sort((a, b) => b.count - a.count);
  // The tier floor NORMALIZES to the data on screen, like the density ramp: an absolute
  // tier2Min under a sparse filter (Reicha's densest city has a handful of concerts)
  // put every city in tier 3, whose layer doesn't render below townMinzoom — the
  // filtered continental view looked EMPTY. Capped at the configured value, so the
  // unfiltered map is untouched.
  const maxCount = byCount.length ? byCount[0].count : 0;
  const tier2Min = Math.min(m.tier2Min, Math.max(2, Math.ceil(maxCount * 0.3)));
  byCount.forEach((city, i) => {
    if (i < m.capitalCount && city.count >= tier2Min) tiers.set(city.id, 1);
    else if (city.count >= tier2Min) tiers.set(city.id, 2);
    else tiers.set(city.id, 3);
  });
  return tiers;
}

/** Below this many placed cities the configured town floor is lifted entirely. The
 *  floor exists to keep thousands of one-concert villages off the continental view,
 *  but a filtered map with a few dozen cities has no crowd to manage — hiding its
 *  tier-3 towns below z4 read as "this composer has no concerts in Europe". */
const TOWN_FLOOR_CITY_LIMIT = 250;

function effectiveTownMinzoom(cityCount: number, cfg: AtlasConfig): number {
  return cityCount <= TOWN_FLOOR_CITY_LIMIT ? 0 : cfg.markers.townMinzoom;
}

/** Single-entry memo for the stable packing: it is a pure function of the data + the
 *  levers below (never the viewport or current zoom), yet updateMarkers fires on every
 *  list refetch — including repeatedly DURING pans — and on every zoom-band change.
 *  Repacking ~5k cities × 37 bands on the main thread each time was the stable-mode
 *  drag lag. City name/lat/lon are fixed per id, so id:count:tier covers the data. */
let PLACEMENT_CACHE: { key: string; placement: StablePlacement } | null = null;

function cachedStablePlacement(
  cities: City[],
  tiers: globalThis.Map<number, number>,
  cfg: AtlasConfig,
  townFloor: number
): StablePlacement {
  const m = cfg.markers;
  const key =
    cities.map((c) => `${c.id}:${c.count}:${tiers.get(c.id)}`).join("|") +
    `#${m.labelScale}:${m.sizeScale}:${m.stablePadding}:${townFloor}:${m.orphanRadius}`;
  if (PLACEMENT_CACHE?.key === key) return PLACEMENT_CACHE.placement;
  const placement = assignStableMinZoom(cities, tiers, cfg, townFloor);
  PLACEMENT_CACHE = { key, placement };
  return placement;
}

/** Update the GeoJSON source with new city data. Dots mode also re-anchors the density
 *  ramp to the new densest city; atlas mode recomputes tiers instead. */
export function updateMarkers(
  map: MapLibreMap,
  mapCities: City[],
  theme: "dark" | "light",
  cfg: AtlasConfig
) {
  const source = map.getSource(SOURCE_ID) as GeoJSONSource | undefined;
  if (!source) return;

  // Null-island guard: "cities" with no real coordinates (online-event pseudo-cities
  // exported at 0,0 — e.g. "Internet"/World) must not float in the Gulf of Guinea.
  // They stay in the list; they just aren't places.
  const placed = mapCities.filter(hasMapLocation);

  const atlasMode = cfg.markers.mode === "atlas";
  const tiers = atlasMode ? assignTiers(placed, cfg) : null;
  const stable = atlasMode && cfg.markers.placement === "stable";
  const band = currentStableBand(map);
  // Sparse-filter town floor: the layer's zoom range and the packer must agree on the
  // effective floor, or one would draw what the other refused to model.
  const townFloor = effectiveTownMinzoom(placed.length, cfg);
  if (atlasMode) {
    const townLayer = map.getLayer(ATLAS_TOWN_LAYER);
    if (townLayer && townLayer.minzoom !== townFloor) {
      map.setLayerZoomRange(ATLAS_TOWN_LAYER, townFloor, 24);
    }
  }
  const placement =
    stable && tiers ? cachedStablePlacement(placed, tiers, cfg, townFloor) : null;
  const sideAtBand = placement ? placement.sides.get(band) : null;
  // Stable placement: only ship the cities admitted at the current zoom band — see the
  // tierFilter note in addAtlasLayers for why this is setData, not a ["zoom"] filter.
  const shipped = placement
    ? placed.filter((city) => (placement.minz.get(city.id) ?? 0) <= band)
    : placed;

  if (!atlasMode) {
    const maxCount = mapCities.reduce((m, c) => Math.max(m, c.count), 0);
    const color = densityColor(theme, maxCount);
    const textColor = densityTextColor(theme, maxCount);
    for (const layer of [CLUSTER_LAYER, POINT_LAYER]) {
      if (!map.getLayer(layer)) continue;
      map.setPaintProperty(layer, "circle-color", color);
      map.setPaintProperty(layer, "circle-stroke-color", color);
    }
    for (const layer of [CLUSTER_COUNT_LAYER, POINT_COUNT_LAYER]) {
      if (!map.getLayer(layer)) continue;
      map.setPaintProperty(layer, "text-color", textColor);
      map.setPaintProperty(layer, "text-halo-color", color);
    }
  }

  // Below the town floor the only tier-3 cities in `shipped` are the isolation-exempt
  // ones (the packer admits no others), so the flag needs no extra bookkeeping — and
  // being derived per band it clears itself the moment the ordinary town layer takes
  // over at the floor.
  const belowFloor = band < townFloor;
  const features = shipped.map((city) => ({
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: [city.lon, city.lat],
      },
      properties: {
        id: city.id,
        name: city.name,
        country_name: city.country_name,
        count: city.count,
        liveCount: city.liveCount ?? city.count,
        histCount: city.histCount ?? 0,
        upCount: city.upCount ?? null,
        ...(tiers ? { tier: tiers.get(city.id) ?? 3 } : {}),
        ...(placement
          ? {
              minz: placement.minz.get(city.id) ?? 0,
              aside: sideAtBand?.get(city.id) ?? 0,
              orphan: belowFloor && (tiers?.get(city.id) ?? 3) === 3 ? 1 : 0,
            }
          : {}),
      },
    }));

  pushResidual(map, placed, placement, band, cfg);

  // Idempotence guard: upstream signals refresh mapCities' identity on unrelated events
  // (list refetches after a pan, for one), and every redundant setData rebuilds the
  // source and re-runs symbol placement from scratch — re-arbitrating residual overlap
  // pairs into DIFFERENT winners mid-pan. If the shipped content is identical to what
  // this source already holds, do nothing. Keyed on the source object so a style
  // reload (fresh empty source) naturally forces a push.
  const signature = features
    .map((f) => {
      const p = f.properties as Record<string, unknown>;
      return `${p.id}:${p.count}:${p.liveCount}:${p.histCount}:${p.upCount}:${p.tier ?? ""}:${p.minz ?? ""}:${p.aside ?? ""}:${p.orphan ?? ""}`;
    })
    .join("|");
  if (SOURCE_SIGNATURE.get(source) === signature) return;
  SOURCE_SIGNATURE.set(source, signature);

  source.setData({ type: "FeatureCollection", features });
}

/** Last-pushed content per source instance — see the idempotence guard above. */
const SOURCE_SIGNATURE = new WeakMap<GeoJSONSource, string>();

/** Grid cell for the stipple cap, in world px at the current band. Chosen so the densest
 *  European screen at z3 holds a few hundred marks rather than the ~3,000 suppressed
 *  cities actually there: a texture the eye reads as "more here", not a marker field. */
const STIPPLE_CELL = 16;

/** Fill the residual source with the cities this band suppressed — the complement of
 *  what the symbol layers ship. Cheap by construction: it is the same `placed` array and
 *  the same `minz` lookup the marker push already walks, so no extra data crosses the
 *  wire and nothing here depends on the viewport. */
function pushResidual(
  map: MapLibreMap,
  placed: City[],
  placement: StablePlacement | null,
  band: number,
  cfg: AtlasConfig
) {
  const source = map.getSource(RESIDUAL_SOURCE_ID) as GeoJSONSource | undefined;
  if (!source) return;

  // Dynamic placement has no notion of a suppressed set (MapLibre decides per frame, per
  // viewport), so the cue simply doesn't apply there — an empty source, not a guess.
  const hidden = placement
    ? placed
        .filter((city) => (placement.minz.get(city.id) ?? 0) > band)
        .sort((a, b) => b.count - a.count || a.id - b.id)
    : [];

  // Grid cap in WORLD px, matching the packing's coordinate space: a cap computed in
  // screen space would re-pick winners on every pan, and a texture that crawls under the
  // cursor is worse than no texture at all.
  const scale = 512 * 2 ** band;
  const taken = new Set<string>();
  const features = hidden.map((city) => {
    const cx = Math.floor((mercX(city.lon) * scale) / STIPPLE_CELL);
    const cy = Math.floor((mercY(city.lat) * scale) / STIPPLE_CELL);
    const key = `${cx}:${cy}`;
    const first = !taken.has(key);
    if (first) taken.add(key);
    return {
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [city.lon, city.lat] },
      properties: { count: city.count, stipple: first ? 1 : 0 },
    };
  });

  const signature = `${cfg.markers.residual}|${band}|${features
    .map((f) => `${f.geometry.coordinates[0]},${f.geometry.coordinates[1]}:${f.properties.stipple}`)
    .join("|")}`;
  if (SOURCE_SIGNATURE.get(source) === signature) return;
  SOURCE_SIGNATURE.set(source, signature);

  source.setData({ type: "FeatureCollection", features });
}

/** The stable-placement zoom band is STORED per map, not derived from getZoom() on
 *  demand: in globe projection MapLibre continuously adjusts the zoom value while
 *  PANNING across latitudes (to keep ground scale looking constant — a 400px northward
 *  drag at z6.05 lands at z5.88 with no zoom gesture at all). Deriving the band from
 *  the live zoom let a drag cross an integer boundary and swap the whole label set,
 *  which is exactly the instability stable placement exists to prevent. The band
 *  therefore only moves via syncStableBand(), which Map.tsx calls for intentional zoom
 *  changes (wheel/pinch/buttons/flyTo) — never during a drag or its inertia. */
const STABLE_BAND = new WeakMap<MapLibreMap, number>();

// Half-zoom band granularity lives in placement.ts (bandFor).
const clampBand = bandFor;

/** Current band for this map (seeding from the live zoom on first use). */
export function currentStableBand(map: MapLibreMap): number {
  let band = STABLE_BAND.get(map);
  if (band === undefined) {
    band = clampBand(map.getZoom());
    STABLE_BAND.set(map, band);
  }
  return band;
}

/** Re-derive the band from the live zoom (call ONLY on intentional zoom changes).
 *  Returns true if the band moved and the marker data needs a re-push. */
export function syncStableBand(map: MapLibreMap): boolean {
  const band = clampBand(map.getZoom());
  if (band === STABLE_BAND.get(map)) return false;
  STABLE_BAND.set(map, band);
  return true;
}

/** Remove concert layers and sources (both modes' ids — absent ones are skipped). */
export function removeConcertLayers(map: MapLibreMap) {
  for (const id of [
    ATLAS_COUNT_LAYER,
    ATLAS_CAPITAL_LAYER,
    ATLAS_CITY_LAYER,
    ATLAS_TOWN_LAYER,
    ATLAS_ORPHAN_LAYER,
    POINT_COUNT_LAYER,
    CLUSTER_COUNT_LAYER,
    POINT_LAYER,
    CLUSTER_LAYER,
    HULL_LINE_LAYER,
    HULL_FILL_LAYER,
    RESIDUAL_STIPPLE_LAYER,
    RESIDUAL_WASH_LAYER,
  ]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
  if (map.getSource(HULL_SOURCE_ID)) map.removeSource(HULL_SOURCE_ID);
  if (map.getSource(RESIDUAL_SOURCE_ID)) map.removeSource(RESIDUAL_SOURCE_ID);
}

export { SOURCE_ID, CLUSTER_LAYER, POINT_LAYER };
